/* EmailSignature — Firm Branding > Email Signature tab
   Keys: email_signature (HTML), email_signature_enforce (bool)
   No new migration needed — keys seeded in Phase 1. */
import { useEffect, useState } from 'react';
import css from '../../../AdminPage.module.css';
import { useFirmBranding, useUpdateFirmBranding } from '../../../../../hooks/useAdmin';
import { useToast } from '../../../../../hooks/useToast';
import RichEmailEditor from '../../../../../components/common/RichEmailEditor';

interface Draft {
  email_signature: string;
  email_signature_enforce: boolean;
}

const DEFAULTS: Draft = {
  email_signature: '',
  email_signature_enforce: false,
};

function toDraft(branding: Record<string, string | null> | undefined): Draft {
  return {
    email_signature: branding?.email_signature ?? '',
    email_signature_enforce: branding?.email_signature_enforce === 'true',
  };
}

function isDirty(a: Draft, b: Draft) {
  return (
    a.email_signature !== b.email_signature ||
    a.email_signature_enforce !== b.email_signature_enforce
  );
}

export default function EmailSignature() {
  const { data: branding, isLoading } = useFirmBranding();
  const update = useUpdateFirmBranding();
  const { showToast } = useToast();

  const [draft, setDraft] = useState<Draft>(DEFAULTS);
  const [original, setOriginal] = useState<Draft>(DEFAULTS);

  /* Seed form from server data once loaded. */
  useEffect(() => {
    if (!branding) return;
    const d = toDraft(branding);
    setDraft(d);
    setOriginal(d);
  }, [branding]);

  async function handleSave() {
    try {
      await update.mutateAsync([
        { key: 'email_signature', value: draft.email_signature || null },
        { key: 'email_signature_enforce', value: draft.email_signature_enforce ? 'true' : 'false' },
      ]);
      setOriginal(draft);
      showToast('Email signature saved', 'success');
    } catch {
      showToast('Failed to save email signature', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <span className={css.spinnerDot} />
        <span className={css.spinnerDot} />
        <span className={css.spinnerDot} />
      </div>
    );
  }

  const dirty = isDirty(draft, original);

  return (
    <div>
      <p style={{ marginBottom: 20, color: 'var(--text-secondary)' }}>
        Compose an HTML email signature that is automatically appended to outbound emails. When
        "Enforce signature" is on, this signature overrides any personal signature set by individual
        users.
      </p>

      {/* ── Rich editor ── */}
      <div className={css.formGroup} style={{ marginBottom: 24 }}>
        <label className={css.formLabel}>Email Signature (HTML)</label>
        <div data-testid="editor-wrapper">
          <RichEmailEditor
            value={draft.email_signature}
            onChange={(html) => setDraft((p) => ({ ...p, email_signature: html }))}
            placeholder="e.g. Best regards,&#10;Acme Law LLP | 0800 123 456"
            height={260}
            showSourceToggle
          />
        </div>
      </div>

      {/* ── Preview ── */}
      {draft.email_signature && (
        <div
          data-testid="signature-preview"
          style={{
            marginBottom: 24,
            padding: '14px 16px',
            background: 'var(--surface-secondary)',
            borderRadius: 8,
            border: '1px solid var(--border-primary)',
            maxWidth: 680,
          }}
        >
          <p
            style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              marginBottom: 10,
              color: 'var(--text-secondary)',
            }}
          >
            Preview
          </p>
          {}
          <div dangerouslySetInnerHTML={{ __html: draft.email_signature }} />
        </div>
      )}

      {/* ── Enforce toggle ── */}
      <div className={css.formGroup} style={{ marginBottom: 28 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            id="input-email_signature_enforce"
            data-testid="input-email_signature_enforce"
            type="checkbox"
            checked={draft.email_signature_enforce}
            onChange={(e) => setDraft((p) => ({ ...p, email_signature_enforce: e.target.checked }))}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
          />
          <span className={css.formLabel} style={{ margin: 0 }}>
            Enforce firm signature
          </span>
        </label>
        <p
          style={{
            marginTop: 6,
            marginLeft: 26,
            fontSize: '0.8125rem',
            color: 'var(--text-muted)',
          }}
        >
          When enabled, this signature is appended to all outbound emails regardless of individual
          user preferences.
        </p>
      </div>

      <button
        className={css.btnPrimary}
        data-testid="btn-save"
        onClick={handleSave}
        disabled={!dirty || update.isPending}
      >
        {update.isPending ? 'Saving…' : 'Save Email Signature'}
      </button>
    </div>
  );
}
